import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { API } from "../App";
import { toast } from "sonner";
import { Briefcase, Award, LogOut, CalendarClock, Plus, Trash2, Check, Loader2 } from "lucide-react";

const TABS = [
  { id: "placement", label: "Placement", icon: Briefcase },
  { id: "certifications", label: "Certifications", icon: Award },
  { id: "withdrawal", label: "Withdrawal", icon: LogOut },
  { id: "checkins", label: "Check-ins", icon: CalendarClock },
];

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);

const ParticipantCasePanel = ({ participantId, participantName }) => {
  const [tab, setTab] = useState("placement");
  const [data, setData] = useState({ outcome: {}, withdrawal: {}, certifications: [], checkins: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCert, setNewCert] = useState({ name: "", status: "pending", exam_date: "", attempts: 1 });

  const headers = { "Content-Type": "application/json" };
  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/case`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setData({ outcome: d.outcome || {}, withdrawal: d.withdrawal || {}, certifications: d.certifications || [], checkins: d.checkins || [] });
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [participantId]); // eslint-disable-line

  const setOutcome = (k, v) => setData(d => ({ ...d, outcome: { ...d.outcome, [k]: v } }));
  const setWd = (k, v) => setData(d => ({ ...d, withdrawal: { ...d.withdrawal, [k]: v } }));

  const saveOutcome = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/outcome`, { method: "PUT", headers, credentials: "include", body: JSON.stringify({ ...data.outcome, placed: true }) });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setData(prev => ({ ...prev, checkins: d.checkins || prev.checkins }));
      toast.success("Placement saved" + (data.outcome.placement_date ? " — check-ins scheduled" : ""));
    } catch { toast.error("Failed to save placement"); } finally { setSaving(false); }
  };

  const saveWithdrawal = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/withdrawal`, { method: "PUT", headers, credentials: "include", body: JSON.stringify(data.withdrawal) });
      if (!res.ok) throw new Error();
      toast.success("Withdrawal / refund saved");
    } catch { toast.error("Failed to save withdrawal"); } finally { setSaving(false); }
  };

  const addCert = async () => {
    if (!newCert.name.trim()) { toast.error("Certification name required"); return; }
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/certifications`, { method: "POST", headers, credentials: "include", body: JSON.stringify(newCert) });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setData(prev => ({ ...prev, certifications: [...prev.certifications, d.certification] }));
      setNewCert({ name: "", status: "pending", exam_date: "", attempts: 1 });
      toast.success("Certification added");
    } catch { toast.error("Failed to add certification"); }
  };

  const updateCertStatus = async (cert, status) => {
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/certifications/${cert.id}`, { method: "PUT", headers, credentials: "include", body: JSON.stringify({ status, earned_date: status === "passed" ? new Date().toISOString().slice(0, 10) : "" }) });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setData(prev => ({ ...prev, certifications: d.certifications }));
    } catch { toast.error("Failed to update certification"); }
  };

  const deleteCert = async (cert) => {
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/certifications/${cert.id}`, { method: "DELETE", credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setData(prev => ({ ...prev, certifications: d.certifications }));
      toast.success("Removed");
    } catch { toast.error("Failed to remove"); }
  };

  const completeCheckin = async (c, stillEmployed) => {
    try {
      const res = await fetch(`${API}/admin/participants/${participantId}/checkins/${c.id}`, { method: "PUT", headers, credentials: "include", body: JSON.stringify({ completed: true, still_employed: stillEmployed }) });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setData(prev => ({ ...prev, checkins: d.checkins }));
      toast.success(`${c.milestone} check-in completed`);
    } catch { toast.error("Failed to update check-in"); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="participant-case-panel">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 mb-4">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${tab === t.id ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`case-tab-${t.id}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "placement" && (
        <div className="space-y-3" data-testid="case-placement">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employer"><Input className="input-base" value={data.outcome.employer || ""} onChange={e => setOutcome("employer", e.target.value)} data-testid="outcome-employer" /></Field>
            <Field label="Job Title"><Input className="input-base" value={data.outcome.job_title || ""} onChange={e => setOutcome("job_title", e.target.value)} data-testid="outcome-job-title" /></Field>
            <Field label="Placement Date"><Input type="date" className="input-base" value={data.outcome.placement_date || ""} onChange={e => setOutcome("placement_date", e.target.value)} data-testid="outcome-placement-date" /></Field>
            <Field label="Credential Earned"><Input className="input-base" value={data.outcome.credential_earned || ""} onChange={e => setOutcome("credential_earned", e.target.value)} data-testid="outcome-credential" /></Field>
            <Field label="Wage Amount"><Input className="input-base" value={data.outcome.wage_amount || ""} onChange={e => setOutcome("wage_amount", e.target.value)} placeholder="e.g. 52000" data-testid="outcome-wage" /></Field>
            <Field label="Wage Period">
              <Select value={data.outcome.wage_period || "year"} onValueChange={v => setOutcome("wage_period", v)}>
                <SelectTrigger className="input-base" data-testid="outcome-wage-period"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="hour">Per hour</SelectItem><SelectItem value="year">Per year</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Hours / Week"><Input className="input-base" value={data.outcome.hours_per_week || ""} onChange={e => setOutcome("hours_per_week", e.target.value)} data-testid="outcome-hours" /></Field>
          </div>
          <Field label="Notes"><Input className="input-base" value={data.outcome.notes || ""} onChange={e => setOutcome("notes", e.target.value)} data-testid="outcome-notes" /></Field>
          <Button className="btn-primary gap-2" onClick={saveOutcome} disabled={saving} data-testid="save-outcome-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Placement
          </Button>
          <p className="text-xs text-slate-400">Setting a placement date automatically schedules 6-month, 1-year and 2-year check-ins.</p>
        </div>
      )}

      {tab === "certifications" && (
        <div className="space-y-3" data-testid="case-certifications">
          {data.certifications.length === 0 && <p className="text-sm text-slate-400">No certifications recorded yet.</p>}
          {data.certifications.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`cert-row-${c.id}`}>
              <div className="min-w-0">
                <p className="font-medium text-slate-800 truncate">{c.name}</p>
                <p className="text-xs text-slate-400">{c.exam_date || "No exam date"} · {c.attempts} attempt(s)</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={c.status} onValueChange={v => updateCertStatus(c, v)}>
                  <SelectTrigger className="w-28 h-8 text-xs bg-surface-muted" data-testid={`cert-status-${c.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="pending">Pending</SelectItem><SelectItem value="passed">Passed</SelectItem><SelectItem value="failed">Failed</SelectItem></SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="border-red-200 text-red-600 h-8" onClick={() => deleteCert(c)} data-testid={`cert-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
          <div className="border-t border-slate-200 pt-3 grid grid-cols-2 gap-2">
            <Input className="input-base" placeholder="Certification name" value={newCert.name} onChange={e => setNewCert(s => ({ ...s, name: e.target.value }))} data-testid="new-cert-name" />
            <Input type="date" className="input-base" value={newCert.exam_date} onChange={e => setNewCert(s => ({ ...s, exam_date: e.target.value }))} data-testid="new-cert-date" />
            <Select value={newCert.status} onValueChange={v => setNewCert(s => ({ ...s, status: v }))}>
              <SelectTrigger className="input-base" data-testid="new-cert-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="pending">Pending</SelectItem><SelectItem value="passed">Passed</SelectItem><SelectItem value="failed">Failed</SelectItem></SelectContent>
            </Select>
            <Button className="btn-primary gap-1" onClick={addCert} data-testid="add-cert-btn"><Plus className="w-4 h-4" /> Add</Button>
          </div>
        </div>
      )}

      {tab === "withdrawal" && (
        <div className="space-y-3" data-testid="case-withdrawal">
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={!!data.withdrawal.withdrawn} onChange={e => setWd("withdrawn", e.target.checked)} data-testid="withdrawn-checkbox" className="w-4 h-4" />
            <Label className="text-sm text-slate-700">Mark this participant as withdrawn</Label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Withdrawal Date"><Input type="date" className="input-base" value={data.withdrawal.withdrawal_date || ""} onChange={e => setWd("withdrawal_date", e.target.value)} data-testid="wd-date" /></Field>
            <Field label="Last Attendance Date"><Input type="date" className="input-base" value={data.withdrawal.last_attendance_date || ""} onChange={e => setWd("last_attendance_date", e.target.value)} data-testid="wd-last-att" /></Field>
            <Field label="Reason"><Input className="input-base" value={data.withdrawal.reason || ""} onChange={e => setWd("reason", e.target.value)} data-testid="wd-reason" /></Field>
            <Field label="Refund Status">
              <Select value={data.withdrawal.refund_status || "none"} onValueChange={v => setWd("refund_status", v)}>
                <SelectTrigger className="input-base" data-testid="wd-refund-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">None</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="issued">Issued</SelectItem><SelectItem value="denied">Denied</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Refund Amount"><Input className="input-base" value={data.withdrawal.refund_amount || ""} onChange={e => setWd("refund_amount", e.target.value)} placeholder="e.g. 99" data-testid="wd-refund-amount" /></Field>
            <Field label="Refund Date"><Input type="date" className="input-base" value={data.withdrawal.refund_date || ""} onChange={e => setWd("refund_date", e.target.value)} data-testid="wd-refund-date" /></Field>
          </div>
          <Button className="btn-primary gap-2" onClick={saveWithdrawal} disabled={saving} data-testid="save-withdrawal-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
          </Button>
        </div>
      )}

      {tab === "checkins" && (
        <div className="space-y-3" data-testid="case-checkins">
          {data.checkins.length === 0 && <p className="text-sm text-slate-400">No check-ins scheduled. Set a placement date to auto-schedule 6-mo / 1-yr / 2-yr check-ins.</p>}
          {data.checkins.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`checkin-row-${c.id}`}>
              <div>
                <p className="font-medium text-slate-800">{c.milestone === "6mo" ? "6-Month" : c.milestone === "1yr" ? "1-Year" : "2-Year"} Check-in</p>
                <p className="text-xs text-slate-400">Due {c.due_date}{c.still_employed ? ` · ${c.still_employed === "yes" ? "Still employed" : "Not employed"}` : ""}</p>
              </div>
              {c.completed ? (
                <Badge className="bg-emerald-100 text-emerald-700 gap-1"><Check className="w-3 h-3" /> Done</Badge>
              ) : (
                <div className="flex gap-1.5">
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => completeCheckin(c, "yes")} data-testid={`checkin-employed-${c.id}`}>Employed</Button>
                  <Button size="sm" variant="outline" className="h-8 border-slate-300" onClick={() => completeCheckin(c, "no")} data-testid={`checkin-not-${c.id}`}>Not employed</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParticipantCasePanel;
